import axios from "axios";
import React, { useEffect, useState } from "react";
import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import { toast } from "react-toastify";
import { FaCircleInfo } from "react-icons/fa6";
import { BASE_URL } from "../../../redux/services/apiPath";
import * as Yup from "yup";
import authHeader from "../../../redux/services/authHeader";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

/* ─────────────────────────────────────────
   Inline styles – no extra CSS file needed
───────────────────────────────────────── */
const S = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg,#f8f9ff 0%,#eef1fb 100%)",
    padding: "32px 24px",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 28,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a1d2e",
    margin: 0,
  },
  pageTitleAccent: { color: "#4f6ef7" },

  /* ── Addon Group card ── */
  groupCard: {
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 2px 16px rgba(79,110,247,.10)",
    border: "1.5px solid #e8ecff",
    marginBottom: 28,
    overflow: "hidden",
  },
  groupCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(90deg,#3ea99f 0%,#3ea77f 100%)",
    padding: "14px 22px",
  },
  groupCardTitle: {
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    margin: 0,
    letterSpacing: 0.3,
  },
  groupCardBody: { padding: "22px 22px 18px" },

  /* ── Grid ── */
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "8px 10px",
    marginBottom: 8,
  },

  /* ── Form controls ── */
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#5a6080",
    marginBottom: 5,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  required: { color: "#f44" },
  input: {
    width: "100%",
    padding: "9px 13px",
    borderRadius: 9,
    border: "1.5px solid #dde2f5",
    fontSize: 14,
    color: "#1a1d2e",
    background: "#f7f9ff",
    outline: "none",
    transition: "border .2s",
    boxSizing: "border-box",
  },
  inputError: {
    border: "1.5px solid #f44",
    background: "#fff5f5",
  },
  select: {
    width: "100%",
    padding: "9px 13px",
    borderRadius: 9,
    border: "1.5px solid #dde2f5",
    fontSize: 14,
    color: "#1a1d2e",
    background: "#f7f9ff",
    outline: "none",
    cursor: "pointer",
    boxSizing: "border-box",
  },
  errorMsg: { color: "#f44", fontSize: 11, marginTop: 4 },
  infoIcon: { color: "#4f6ef7", verticalAlign: "middle", marginLeft: 5 },

  /* ── Item list ── */
  itemsSection: {
    marginTop: 18,
    borderTop: "1.5px dashed #e0e5ff",
    paddingTop: 18,
  },
  itemsSectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#4f6ef7",
    marginBottom: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  itemRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr auto",
    gap: "10px 14px",
    alignItems: "end",
    background: "#f7f9ff",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 10,
    border: "1px solid #e8ecff",
  },

  /* ── Buttons ── */
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 18px",
    borderRadius: 9,
    border: "none",
    background: "#3ea99f",
    color: "#fff",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(79,110,247,.25)",
    transition: "opacity .15s,transform .1s",
    whiteSpace: "nowrap",
  },
  btnPrimaryDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
  btnDanger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 9,
    border: "1.5px solid #ffcdd2",
    background: "#fff5f5",
    color: "#e53935",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    transition: "background .15s",
    whiteSpace: "nowrap",
  },
  btnOutline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    borderRadius: 9,
    border: "1.5px solid #4f6ef7",
    background: "#fff",
    color: "#4f6ef7",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    transition: "background .15s",
    whiteSpace: "nowrap",
  },
  btnSubmit: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 36px",
    borderRadius: 12,
    border: "none",
    background: "linear-gradient(90deg,#3ea88f,#3ea99f)",
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
    boxShadow: "0 4px 18px rgba(79,110,247,.35)",
    letterSpacing: 0.3,
  },

  /* ── Footer action bar ── */
  footerBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 28,
    padding: "18px 22px",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 2px 16px rgba(79,110,247,.10)",
    border: "1.5px solid #e8ecff",
  },
  footerLeft: { display: "flex", gap: 10, flexWrap: "wrap" },

  /* ── Inline actions bar below each group ── */
  groupActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 14,
    paddingTop: 14,
    borderTop: "1px solid #eef0ff",
  },
};

/* ─── Validation ─────────────────────────────────── */
const validationSchema = Yup.object({
  addOnGroups: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required("Name is required"),
      addons: Yup.array()
        .of(
          Yup.object().shape({
            name: Yup.string().required("Addon name required"),
            price: Yup.number()
              .typeError("Enter a valid price")
              .positive("Price must be positive")
              .required("Price is required"),
            isActive: Yup.boolean().required("Select Active/Inactive"),
          }),
        )
        .min(1, "At least one addon is required"),
      isActive: Yup.string().required("Select Active/Inactive"),
      maxAddon: Yup.number()
        .min(1, "Minimum one is required")
        .required("Required Field"),
      minimum_needed: Yup.number()
        .min(1, "Minimum one is required")
        .required("Required Field"),
      maximum_allowed: Yup.number()
        .min(1, "Minimum one is required")
        .required("Required Field"),
      multiple: Yup.string().required("Select Multiple/Single"),
    }),
  ),
});

/* ─── FoodType badge colors ──────────────────────── */
const foodTypeLabel = { 1: "Veg", 2: "Non-Veg", 3: "Egg", 4: "None" };

/* ═══════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════ */
const MultipleAddons = () => {
  const history = useHistory();
  const location = useLocation();
  const menu = location.state?.menu;

  const [initialValues, setInitialValues] = useState({
    addOnGroups: [
      {
        name: "",
        title: "",
        addons: [
          {
            name: "",
            title: "",
            price: "",
            isActive: true,
            sort_order: "",
            food_type: 1,
          },
        ],
        isActive: "",
        isRequired: "",
        multiple: "",
        maxAddon: "",
        minimum_needed: "",
        maximum_allowed: "",
        sort_order: "",
      },
    ],
  });

  useEffect(() => {
    if (menu?.addOnGroups?.length > 0) {
      setInitialValues({ addOnGroups: [...menu.addOnGroups] });
    }
  }, [menu]);

  const handleSubmit = async (values) => {
    const data = {
      vendorId: localStorage.getItem("vendorId") || "",
      menuId: menu?._id,
      ...values,
    };
    try {
      const res = await axios.post(
        `${BASE_URL}addon/updateAddOn`,
        data,
        authHeader(),
      );
      if (res?.data?.status === "success") {
        toast.success(res?.data?.message);
        history.goBack();
      } else {
        toast.error("Something went wrong");
      }
    } catch {
      toast.error("Request failed");
    }
  };

  const newGroup = () => ({
    name: "",
    title: "",
    maxAddon: "",
    minimum_needed: "",
    maximum_allowed: "",
    sort_order: "",
    addons: [
      { name: "", price: "", isActive: true, sort_order: "", food_type: 1 },
    ],
    isActive: "",
    isRequired: "",
    multiple: "",
  });

  const newAddon = () => ({
    name: "",
    title: "",
    price: "",
    isActive: true,
    sort_order: "",
    food_type: 1,
  });

  return (
    <div style={S.page}>
      {/* ── Page header ── */}
      <div style={S.pageHeader}>
        <div>
          <h4 style={S.pageTitle}>
            Create Addons
            {menu?.name && (
              <>
                {" "}
                for <span style={S.pageTitleAccent}>"{menu.name}"</span>
              </>
            )}
          </h4>
          <p style={{ color: "#3ea99f", fontSize: 13, margin: "4px 0 0" }}>
            Configure addon groups and items for this menu product.
          </p>
        </div>
      </div>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form noValidate>
            <FieldArray name="addOnGroups">
              {({ push, remove }) => (
                <>
                  {formik.values.addOnGroups.map((group, index) => (
                    <div key={index} style={S.groupCard}>
                      {/* Card header */}
                      <div style={S.groupCardHeader}>
                        <span style={S.groupCardTitle}>
                          Addon Group #{index + 1}
                          {group.name && ` — ${group.name}`}
                        </span>
                        <span
                          style={{
                            color: "rgba(255,255,255,.7)",
                            fontSize: 12,
                          }}
                        >
                          {group.addons?.length || 0} item(s)
                        </span>
                      </div>

                      {/* Card body */}
                      <div style={S.groupCardBody}>
                        {/* ── Group meta fields ── */}
                        <div style={S.grid}>
                          {/* Addon Title */}
                          <div>
                            <label style={S.label}>
                              Addon Title <span style={S.required}>*</span>
                            </label>
                            <Field
                              type="text"
                              name={`addOnGroups[${index}].name`}
                              placeholder="e.g. Size"
                              onChange={(e) => {
                                formik.handleChange(e);
                                formik.setFieldValue(
                                  `addOnGroups[${index}].title`,
                                  e.target.value,
                                );
                              }}
                              style={{
                                ...S.input,
                                ...(formik.errors.addOnGroups?.[index]?.name &&
                                formik.touched.addOnGroups?.[index]?.name
                                  ? S.inputError
                                  : {}),
                              }}
                            />
                            <ErrorMessage
                              name={`addOnGroups[${index}].name`}
                              render={(msg) => (
                                <div style={S.errorMsg}>⚠ {msg}</div>
                              )}
                            />
                          </div>

                          {/* Is Active */}
                          <div>
                            <label style={S.label}>
                              Status <span style={S.required}>*</span>
                            </label>
                            <select
                              name={`addOnGroups[${index}].isActive`}
                              value={group.isActive}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `addOnGroups[${index}].isActive`,
                                  e.target.value,
                                )
                              }
                              style={{
                                ...S.select,
                                ...(formik.errors.addOnGroups?.[index]
                                  ?.isActive &&
                                formik.touched.addOnGroups?.[index]?.isActive
                                  ? S.inputError
                                  : {}),
                              }}
                            >
                              <option value="">Select</option>
                              <option value="true">Active</option>
                              <option value="false">Inactive</option>
                            </select>
                            <ErrorMessage
                              name={`addOnGroups[${index}].isActive`}
                              render={(msg) => (
                                <div style={S.errorMsg}>⚠ {msg}</div>
                              )}
                            />
                          </div>

                          {/* Is Required */}
                          <div>
                            <label style={S.label}>
                              Required? <span style={S.required}>*</span>
                            </label>
                            <select
                              name={`addOnGroups[${index}].isRequired`}
                              value={group.isRequired}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `addOnGroups[${index}].isRequired`,
                                  e.target.value,
                                )
                              }
                              style={S.select}
                            >
                              <option value="">Select</option>
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          </div>

                          {/* Multiple */}
                          <div>
                            <label style={S.label}>
                              Multiple? <span style={S.required}>*</span>
                            </label>
                            <select
                              name={`addOnGroups[${index}].multiple`}
                              value={group.multiple}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `addOnGroups[${index}].multiple`,
                                  e.target.value,
                                )
                              }
                              style={{
                                ...S.select,
                                ...(formik.errors.addOnGroups?.[index]
                                  ?.multiple &&
                                formik.touched.addOnGroups?.[index]?.multiple
                                  ? S.inputError
                                  : {}),
                              }}
                            >
                              <option value="">Select</option>
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                            <ErrorMessage
                              name={`addOnGroups[${index}].multiple`}
                              render={(msg) => (
                                <div style={S.errorMsg}>⚠ {msg}</div>
                              )}
                            />
                          </div>

                          {/* Min Needed */}
                          <div>
                            <label style={S.label}>
                              Min Needed <span style={S.required}>*</span>
                            </label>
                            <Field
                              type="number"
                              name={`addOnGroups[${index}].minimum_needed`}
                              placeholder="e.g. 1"
                              style={S.input}
                            />
                            <ErrorMessage
                              name={`addOnGroups[${index}].minimum_needed`}
                              render={(msg) => (
                                <div style={S.errorMsg}>⚠ {msg}</div>
                              )}
                            />
                          </div>

                          {/* Max Allowed */}
                          <div>
                            <label style={S.label}>
                              Max Allowed <span style={S.required}>*</span>
                            </label>
                            <Field
                              type="number"
                              name={`addOnGroups[${index}].maximum_allowed`}
                              placeholder="e.g. 3"
                              style={S.input}
                            />
                            <ErrorMessage
                              name={`addOnGroups[${index}].maximum_allowed`}
                              render={(msg) => (
                                <div style={S.errorMsg}>⚠ {msg}</div>
                              )}
                            />
                          </div>

                          {/* Max Addons */}
                          <div>
                            <label style={S.label}>
                              Max Addons <span style={S.required}>*</span>
                            </label>
                            <Field
                              type="number"
                              name={`addOnGroups[${index}].maxAddon`}
                              placeholder="e.g. 5"
                              style={S.input}
                            />
                            <ErrorMessage
                              name={`addOnGroups[${index}].maxAddon`}
                              render={(msg) => (
                                <div style={S.errorMsg}>⚠ {msg}</div>
                              )}
                            />
                          </div>

                          {/* Sort Order */}
                          <div>
                            <label style={S.label}>Sort Order</label>
                            <Field
                              type="number"
                              name={`addOnGroups[${index}].sort_order`}
                              placeholder="e.g. 1"
                              style={S.input}
                            />
                          </div>
                        </div>

                        {/* ── Addon Items ── */}
                        <div style={S.itemsSection}>
                          <div style={S.itemsSectionTitle}>📦 Addon Items</div>

                          <FieldArray name={`addOnGroups[${index}].addons`}>
                            {({ push: pushAddon, remove: removeAddon }) => (
                              <>
                                {group.addons?.map((ad, aIndex) => (
                                  <div key={aIndex} style={S.itemRow}>
                                    {/* Name */}
                                    <div>
                                      <label style={S.label}>
                                        Item Name{" "}
                                        <span style={S.required}>*</span>
                                      </label>
                                      <Field
                                        name={`addOnGroups[${index}].addons[${aIndex}].name`}
                                        onChange={(e) => {
                                          formik.handleChange(e);
                                          formik.setFieldValue(
                                            `addOnGroups[${index}].addons[${aIndex}].title`,
                                            e.target.value,
                                          );
                                        }}
                                        placeholder="e.g. Extra Cheese"
                                        style={{
                                          ...S.input,
                                          ...(formik.errors.addOnGroups?.[index]
                                            ?.addons?.[aIndex]?.name &&
                                          formik.touched.addOnGroups?.[index]
                                            ?.addons?.[aIndex]?.name
                                            ? S.inputError
                                            : {}),
                                        }}
                                      />
                                      <ErrorMessage
                                        name={`addOnGroups[${index}].addons[${aIndex}].name`}
                                        render={(msg) => (
                                          <div style={S.errorMsg}>⚠ {msg}</div>
                                        )}
                                      />
                                    </div>

                                    {/* Price */}
                                    <div>
                                      <label style={S.label}>
                                        Price <span style={S.required}>*</span>
                                      </label>
                                      <Field
                                        type="number"
                                        name={`addOnGroups[${index}].addons[${aIndex}].price`}
                                        placeholder="₹ 0.00"
                                        style={{
                                          ...S.input,
                                          ...(formik.errors.addOnGroups?.[index]
                                            ?.addons?.[aIndex]?.price &&
                                          formik.touched.addOnGroups?.[index]
                                            ?.addons?.[aIndex]?.price
                                            ? S.inputError
                                            : {}),
                                        }}
                                      />
                                      <ErrorMessage
                                        name={`addOnGroups[${index}].addons[${aIndex}].price`}
                                        render={(msg) => (
                                          <div style={S.errorMsg}>⚠ {msg}</div>
                                        )}
                                      />
                                    </div>

                                    {/* Food Type */}
                                    <div>
                                      <label style={S.label}>Food Type</label>
                                      <Field
                                        as="select"
                                        name={`addOnGroups[${index}].addons[${aIndex}].food_type`}
                                        style={S.select}
                                      >
                                        <option value={1}>🟢 Veg</option>
                                        <option value={2}>🔴 Non-Veg</option>
                                        <option value={3}>🟡 Egg</option>
                                        <option value={4}>⚪ None</option>
                                      </Field>
                                    </div>

                                    {/* Is Active */}
                                    <div>
                                      <label style={S.label}>Status</label>
                                      <Field
                                        as="select"
                                        name={`addOnGroups[${index}].addons[${aIndex}].isActive`}
                                        style={S.select}
                                      >
                                        <option value={true}>Active</option>
                                        <option value={false}>Inactive</option>
                                      </Field>
                                    </div>

                                    <div>
                                      <label style={S.label}>Sort Order</label>
                                      <Field
                                        type="number"
                                        name={`addOnGroups[${index}].addons[${aIndex}].sort_order`}
                                        placeholder="e.g. 1"
                                        style={S.input}
                                      />
                                    </div>
                                    {/* Remove item button */}
                                    <div
                                      style={{
                                        paddingTop: 20,
                                        position: "absolute",
                                      }}
                                    >
                                      {group.addons.length > 1 && (
                                        <button
                                          type="button"
                                          style={S.btnDanger}
                                          onClick={() => removeAddon(aIndex)}
                                          title="Remove this item"
                                        >
                                          ✕
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}

                                {/* Add Item Button */}
                                <button
                                  type="button"
                                  disabled={
                                    group?.maxAddon <= group.addons.length
                                  }
                                  style={{
                                    ...S.btnOutline,
                                    ...(group?.maxAddon <= group.addons.length
                                      ? S.btnPrimaryDisabled
                                      : {}),
                                  }}
                                  onClick={() => pushAddon(newAddon())}
                                >
                                  + Add Item
                                </button>
                              </>
                            )}
                          </FieldArray>
                        </div>

                        {/* ── Per-group action buttons ── */}
                        <div style={S.groupActions}>
                          {/* Add next group (only on last card) */}
                          {index === formik.values.addOnGroups.length - 1 && (
                            <button
                              type="button"
                              style={S.btnPrimary}
                              onClick={() => push(newGroup())}
                            >
                              + Add Addon Group
                            </button>
                          )}

                          {/* Remove group */}
                          {formik.values.addOnGroups.length > 1 && (
                            <button
                              type="button"
                              style={S.btnDanger}
                              onClick={() => remove(index)}
                            >
                              🗑 Remove Group
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </FieldArray>

            {/* ── Footer action bar ── */}
            <div style={S.footerBar}>
              <p style={{ margin: 0, color: "#3ea99f", fontSize: 13 }}>
                {formik.values.addOnGroups.length} group(s) configured
              </p>
              <div style={S.footerLeft}>
                <button
                  type="submit"
                  style={S.btnSubmit}
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? "Saving…" : "✓ Save Addons"}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default MultipleAddons;
